const API_URL = import.meta.env.VITE_API_URL || 'https://api.mycoursesearch.com';

// Types
export interface User {
  id: string;
  email: string;
  name: string;
  customer_id: string;
  company_name: string;
  plan: 'pro' | 'advanced' | 'platinum';
  role: 'owner' | 'admin' | 'editor' | 'viewer';
  is_admin?: boolean;
  email_verified?: boolean;
  created_at?: string;
}

export type MemberRole = 'owner' | 'admin' | 'editor' | 'viewer';

export interface AccountMember {
  id: string;
  user_id: string;
  email: string;
  name: string;
  role: MemberRole;
  invited_at: string;
  accepted_at: string | null;
}

export interface PendingInvitation {
  id: string;
  email: string;
  role: MemberRole;
  expires_at: string;
  created_at: string;
}

export interface AccountOption {
  customer_id: string;
  company_name: string;
  role: string;
}

export interface LoginResponse {
  token?: string;
  user?: User;
  requires_account_selection?: boolean;
  accounts?: AccountOption[];
  temp_token?: string;
}

export interface ApiKey {
  id: string;
  key?: string; // Only returned on creation
  key_prefix: string;
  name: string;
  is_active: boolean;
  created_at: string;
  last_used_at: string | null;
}

export interface Domain {
  id: string;
  domain: string;
  is_active: boolean;
  created_at: string;
}

export interface Course {
  id: string;
  title: string;
  description: string | null;
  image_url: string | null;
  clickfunnels_connection_id: string | null;
  created_at: string;
}

export interface Lesson {
  id: string;
  course_id: string;
  title: string;
  description: string | null;
  content: string | null;
  url: string;
  order_index: number;
  created_at: string;
  updated_at: string;
}

export interface ResourceUsage {
  current: number;
  limit: number; // -1 = unlimited
}

export interface Stats {
  api_keys: ResourceUsage;
  domains: ResourceUsage;
  courses: ResourceUsage;
  lessons: ResourceUsage;
  widgets: ResourceUsage;
  team_members: ResourceUsage;
  searches_today: number;
  searches_month: ResourceUsage;
}

export interface LimitsConfig {
  pro_courses: string; pro_lessons: string; pro_widgets: string; pro_api_keys: string;
  pro_domains: string; pro_searches_monthly: string; pro_team_members: string;
  advanced_courses: string; advanced_lessons: string; advanced_widgets: string; advanced_api_keys: string;
  advanced_domains: string; advanced_searches_monthly: string; advanced_team_members: string;
  platinum_courses: string; platinum_lessons: string; platinum_widgets: string; platinum_api_keys: string;
  platinum_domains: string; platinum_searches_monthly: string; platinum_team_members: string;
}

export interface Analytics {
  daily_stats: { date: string; search_count: number; unique_queries: number }[];
  top_queries: { query: string; count: number }[];
  recent_searches: { query: string; results_count: number; domain: string | null; created_at: string }[];
}

export interface WidgetSettings {
  theme: 'light' | 'dark';
  accent_color: string;
  placeholder: string;
  results_limit: number;
  mode: 'inline' | 'modal';
  shortcut: string;
  show_powered_by: boolean;
}

export type BubblePosition = 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';

export interface Widget {
  id: string;
  customer_id: string;
  name: string;
  theme: 'light' | 'dark';
  accent_color: string;
  placeholder: string;
  results_limit: number;
  mode: 'inline' | 'modal' | 'bubble';
  shortcut: string;
  show_powered_by: boolean;
  bubble_position: BubblePosition;
  bubble_offset_x: number;
  bubble_offset_y: number;
  max_width: number;
  courseIds: string[];
  created_at: string;
  updated_at: string;
}

export interface CreateWidgetRequest {
  name: string;
  theme?: 'light' | 'dark';
  accent_color?: string;
  placeholder?: string;
  results_limit?: number;
  mode?: 'inline' | 'modal' | 'bubble';
  shortcut?: string;
  show_powered_by?: boolean;
  bubble_position?: BubblePosition;
  bubble_offset_x?: number;
  bubble_offset_y?: number;
  max_width?: number;
  courseIds?: string[];
}

export interface Subscription {
  plan: 'pro' | 'advanced' | 'platinum';
  status: 'active' | 'trialing' | 'past_due' | 'canceled' | 'unpaid' | 'incomplete';
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  stripe_subscription_id: string | null;
}

export interface WebhookLog {
  id: string;
  event_type: string;
  stripe_event_id: string | null;
  customer_id: string | null;
  status: string;
  payload: string | null;
  error_message: string | null;
  created_at: string;
}

export interface AuthResponse {
  token: string;
  user: User;
}

// ClickFunnels types
export interface ClickFunnelsIntegration {
  id: string;
  name: string;
  api_key: string; // Masked
  workspace: string;
  customer_center_url: string | null;
  scrape_email: string | null;
  scrape_password: string | null;
  is_connected: boolean;
  last_tested_at: string | null;
  created_at: string;
}

export interface CFTeam {
  id: number;
  public_id: string;
  name: string;
}

export interface CFWorkspace {
  id: number;
  public_id: string;
  team_id: number;
  name: string;
  subdomain: string;
}

export interface CFCourse {
  id: number;
  public_id: string;
  title: string;
  description: string;
  current_path: string;
  image_url?: string;
  show_in_community?: boolean;
}

export interface CFSection {
  id: number;
  public_id: string;
  course_id: number;
  title: string;
  description: string;
  current_path: string;
}

export interface CFLesson {
  id: number;
  public_id: string;
  section_id: number;
  title: string;
  description: string;
  current_path: string;
}

// API client class
class ApiClient {
  private token: string | null = null;

  setToken(token: string | null) {
    this.token = token;
    if (token) {
      localStorage.setItem('auth_token', token);
    } else {
      localStorage.removeItem('auth_token');
    }
  }

  getToken(): string | null {
    if (!this.token) {
      this.token = localStorage.getItem('auth_token');
    }
    return this.token;
  }

  private async request<T>(endpoint: string, options: RequestInit = {}, skipAutoAuth = false): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...options.headers as Record<string, string>,
    };

    if (!skipAutoAuth) {
      const token = this.getToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'An error occurred');
    }

    return data;
  }

  // Auth endpoints
  async signup(email: string, password: string, companyName: string, fullName: string): Promise<AuthResponse> {
    const data = await this.request<AuthResponse>('/api/auth/signup', {
      method: 'POST',
      body: JSON.stringify({ email, password, company_name: companyName, name: fullName }),
    });
    this.setToken(data.token);
    return data;
  }

  async login(email: string, password: string): Promise<LoginResponse> {
    const data = await this.request<LoginResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    if (data.token) {
      this.setToken(data.token);
    }
    return data;
  }

  async logout(): Promise<void> {
    try {
      await this.request('/api/auth/logout', { method: 'POST' });
    } finally {
      this.setToken(null);
    }
  }

  async getCurrentUser(): Promise<User> {
    return this.request<User>('/api/auth/me');
  }

  // Password reset & email verification (public endpoints)
  async forgotPassword(email: string): Promise<{ message: string }> {
    return this.request('/api/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async resetPassword(token: string, newPassword: string): Promise<{ message: string }> {
    return this.request('/api/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, new_password: newPassword }),
    });
  }

  async verifyEmail(token: string): Promise<{ message: string }> {
    return this.request('/api/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  }

  async resendVerification(): Promise<{ message: string }> {
    return this.request('/api/auth/resend-verification', {
      method: 'POST',
    });
  }

  async selectAccount(customerId: string, tempToken: string): Promise<AuthResponse> {
    const data = await this.request<AuthResponse>('/api/auth/select-account', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${tempToken}`,
      },
      body: JSON.stringify({ customer_id: customerId }),
    }, true);
    if (data.token) {
      this.setToken(data.token);
    }
    return data;
  }

  // Members endpoints
  async listMembers(): Promise<{ members: AccountMember[]; invitations: PendingInvitation[] }> {
    return this.request('/api/dashboard/members');
  }

  async inviteMember(email: string, role: string): Promise<any> {
    return this.request('/api/dashboard/members', {
      method: 'POST',
      body: JSON.stringify({ email, role }),
    });
  }

  async updateMemberRole(membershipId: string, role: string): Promise<any> {
    return this.request(`/api/dashboard/members/${membershipId}`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    });
  }

  async removeMember(membershipId: string): Promise<void> {
    await this.request(`/api/dashboard/members/${membershipId}`, {
      method: 'DELETE',
    });
  }

  async revokeInvitation(invitationId: string): Promise<void> {
    await this.request(`/api/dashboard/invitations/${invitationId}`, {
      method: 'DELETE',
    });
  }

  async resendInvitation(invitationId: string): Promise<{ message: string; expires_at: string }> {
    return this.request(`/api/dashboard/invitations/${invitationId}/resend`, {
      method: 'POST',
    });
  }

  // Dashboard endpoints
  async getStats(): Promise<Stats> {
    return this.request<Stats>('/api/dashboard/stats');
  }

  async getAnalytics(days = 30): Promise<Analytics> {
    return this.request<Analytics>(`/api/dashboard/analytics?days=${days}`);
  }

  // API Keys
  async getApiKeys(): Promise<ApiKey[]> {
    return this.request<ApiKey[]>('/api/dashboard/api-keys');
  }

  async createApiKey(name: string): Promise<ApiKey> {
    return this.request<ApiKey>('/api/dashboard/api-keys', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }

  async toggleApiKey(id: string): Promise<{ id: string; is_active: boolean }> {
    return this.request(`/api/dashboard/api-keys/${id}`, { method: 'PATCH' });
  }

  async deleteApiKey(id: string): Promise<void> {
    await this.request(`/api/dashboard/api-keys/${id}`, { method: 'DELETE' });
  }

  // Domains
  async getDomains(): Promise<Domain[]> {
    return this.request<Domain[]>('/api/dashboard/domains');
  }

  async addDomain(domain: string): Promise<Domain> {
    return this.request<Domain>('/api/dashboard/domains', {
      method: 'POST',
      body: JSON.stringify({ domain }),
    });
  }

  async toggleDomain(id: string): Promise<{ id: string; is_active: boolean }> {
    return this.request(`/api/dashboard/domains/${id}`, { method: 'PATCH' });
  }

  async deleteDomain(id: string): Promise<void> {
    await this.request(`/api/dashboard/domains/${id}`, { method: 'DELETE' });
  }

  // Courses
  async getCourses(): Promise<Course[]> {
    return this.request<Course[]>('/api/dashboard/courses');
  }

  async createCourse(title: string, description?: string, clickfunnels_connection_id?: string): Promise<Course> {
    return this.request<Course>('/api/dashboard/courses', {
      method: 'POST',
      body: JSON.stringify({ title, description, clickfunnels_connection_id }),
    });
  }

  async deleteCourse(id: string): Promise<void> {
    await this.request(`/api/dashboard/courses/${id}`, { method: 'DELETE' });
  }

  // Lessons
  async getLessons(courseId?: string): Promise<Lesson[]> {
    const query = courseId ? `?course_id=${courseId}` : '';
    return this.request<Lesson[]>(`/api/dashboard/lessons${query}`);
  }

  async createLesson(data: {
    course_id: string;
    title: string;
    description?: string;
    content?: string;
    url: string;
    order_index?: number;
  }): Promise<Lesson> {
    return this.request<Lesson>('/api/dashboard/lessons', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateLesson(id: string, data: Partial<Omit<Lesson, 'id' | 'course_id' | 'created_at' | 'updated_at'>>): Promise<void> {
    await this.request(`/api/dashboard/lessons/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteLesson(id: string): Promise<void> {
    await this.request(`/api/dashboard/lessons/${id}`, { method: 'DELETE' });
  }

  // ClickFunnels Integrations
  async getClickFunnelsIntegrations(): Promise<{
    integrations: ClickFunnelsIntegration[];
    limit: number;
    canAdd: boolean;
  }> {
    return this.request('/api/clickfunnels/integrations');
  }

  async createClickFunnelsIntegration(data: {
    name: string;
    api_key: string;
    workspace: string;
    customer_center_url?: string;
    scrape_email?: string;
    scrape_password?: string;
  }): Promise<{ integration: ClickFunnelsIntegration }> {
    return this.request('/api/clickfunnels/integrations', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateClickFunnelsIntegration(id: string, data: {
    name?: string;
    api_key?: string;
    workspace?: string;
    customer_center_url?: string;
    scrape_email?: string;
    scrape_password?: string;
  }): Promise<{ integration: ClickFunnelsIntegration }> {
    return this.request(`/api/clickfunnels/integrations/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteClickFunnelsIntegration(id: string): Promise<void> {
    await this.request(`/api/clickfunnels/integrations/${id}`, { method: 'DELETE' });
  }

  async testClickFunnelsConnection(id: string): Promise<{ success: boolean; error?: string }> {
    return this.request(`/api/clickfunnels/integrations/${id}/test`, { method: 'POST' });
  }

  async testClickFunnelsCredentials(id: string): Promise<{
    success: boolean;
    error?: string;
    details: {
      loginUrl: string;
      initialUrl: string;
      loginPageLoaded: boolean;
      passwordFieldVisible: boolean;
      formFound: boolean;
      loginSubmitted: boolean;
      postLoginUrl: string;
      pageErrors: string[];
    };
  }> {
    return this.request(`/api/dashboard/test-credentials/${id}`, { method: 'POST' });
  }

  async getClickFunnelsTeams(integrationId: string): Promise<{
    teams: CFTeam[];
  }> {
    return this.request(`/api/clickfunnels/integrations/${integrationId}/teams`);
  }

  async getClickFunnelsWorkspaces(integrationId: string, teamId: number): Promise<{
    workspaces: CFWorkspace[];
  }> {
    return this.request(`/api/clickfunnels/integrations/${integrationId}/teams/${teamId}/workspaces`);
  }

  async getClickFunnelsCourses(integrationId: string, workspaceId: number): Promise<{
    courses: CFCourse[];
  }> {
    return this.request(`/api/clickfunnels/integrations/${integrationId}/workspaces/${workspaceId}/courses`);
  }

  async getClickFunnelsSections(integrationId: string, courseId: number): Promise<{
    sections: CFSection[];
  }> {
    return this.request(`/api/clickfunnels/integrations/${integrationId}/courses/${courseId}/sections`);
  }

  async getClickFunnelsLessons(integrationId: string, sectionId: number): Promise<{
    lessons: CFLesson[];
  }> {
    return this.request(`/api/clickfunnels/integrations/${integrationId}/sections/${sectionId}/lessons`);
  }

  async importLessonsFromClickFunnels(courseId: string, lessons: Array<{
    title: string;
    description?: string;
    url: string;
    image_url?: string;
  }>): Promise<{ successful: number; failed: number; errors: string[] }> {
    return this.request('/api/clickfunnels/import', {
      method: 'POST',
      body: JSON.stringify({ course_id: courseId, lessons }),
    });
  }

  // Widget Settings (legacy singleton)
  async getWidgetSettings(): Promise<WidgetSettings> {
    return this.request<WidgetSettings>('/api/dashboard/widget-settings');
  }

  async updateWidgetSettings(settings: Partial<WidgetSettings>): Promise<WidgetSettings> {
    return this.request<WidgetSettings>('/api/dashboard/widget-settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  // Widgets (multi-widget CRUD)
  async listWidgets(): Promise<Widget[]> {
    return this.request<Widget[]>('/api/dashboard/widgets');
  }

  async createWidget(data: CreateWidgetRequest): Promise<Widget> {
    return this.request<Widget>('/api/dashboard/widgets', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateWidget(id: string, data: Partial<CreateWidgetRequest>): Promise<Widget> {
    return this.request<Widget>(`/api/dashboard/widgets/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteWidget(id: string): Promise<void> {
    await this.request(`/api/dashboard/widgets/${id}`, { method: 'DELETE' });
  }

  // CSV Import
  async importLessonsFromCSV(courseId: string, lessons: Array<{
    title: string;
    description?: string;
    content?: string;
    url: string;
    order_index?: number;
  }>): Promise<{ successful: number; failed: number; total: number; errors: string[] }> {
    return this.request('/api/dashboard/import', {
      method: 'POST',
      body: JSON.stringify({ course_id: courseId, lessons }),
    });
  }

  // ============ Video Media / Transcription ============

  async getMediaPlugins(): Promise<{ plugins: MediaPlugin[] }> {
    return this.request('/api/dashboard/media/plugins');
  }

  async getMediaStats(): Promise<MediaStats> {
    return this.request<MediaStats>('/api/dashboard/media/stats');
  }

  async scanAllLessonsForVideos(courseId?: string): Promise<{ scanned: number; videosFound: number }> {
    const qs = courseId ? `?course_id=${courseId}` : '';
    return this.request(`/api/dashboard/media/scan${qs}`, { method: 'POST' });
  }

  async scanLessonForVideos(lessonId: string): Promise<{ detected: number; videos: DetectedVideoInfo[] }> {
    return this.request(`/api/dashboard/media/scan-lesson/${lessonId}`, { method: 'POST' });
  }

  async getLessonMedia(lessonId: string): Promise<{ media: LessonMediaInfo[] }> {
    return this.request(`/api/dashboard/media/lesson/${lessonId}`);
  }

  async fetchTranscript(mediaId: string): Promise<TranscriptResultInfo> {
    return this.request(`/api/dashboard/media/transcribe/${mediaId}`, { method: 'POST' });
  }

  async fetchTranscriptsForLesson(lessonId: string): Promise<{ processed: number; succeeded: number; failed: number }> {
    return this.request(`/api/dashboard/media/transcribe-lesson/${lessonId}`, { method: 'POST' });
  }

  async enrichLessonContent(lessonId: string): Promise<{ enriched: boolean }> {
    return this.request(`/api/dashboard/media/enrich/${lessonId}`, { method: 'POST' });
  }

  // ============ Admin ============

  async getAdminStats(): Promise<AdminPlatformStats> {
    return this.request<AdminPlatformStats>('/api/admin/stats');
  }

  async getAdminCustomers(params?: {
    page?: number;
    limit?: number;
    search?: string;
    plan?: string;
  }): Promise<AdminCustomersResponse> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.search) query.set('search', params.search);
    if (params?.plan) query.set('plan', params.plan);
    const qs = query.toString();
    return this.request<AdminCustomersResponse>(`/api/admin/customers${qs ? `?${qs}` : ''}`);
  }

  async getAdminCustomerDetail(id: string): Promise<AdminCustomerDetail> {
    return this.request<AdminCustomerDetail>(`/api/admin/customers/${id}`);
  }

  async updateCustomerPlan(id: string, plan: string): Promise<{ id: string; plan: string }> {
    return this.request(`/api/admin/customers/${id}/plan`, {
      method: 'PATCH',
      body: JSON.stringify({ plan }),
    });
  }

  async toggleCustomerActive(id: string): Promise<{ id: string; is_active: boolean }> {
    return this.request(`/api/admin/customers/${id}/toggle`, { method: 'PATCH' });
  }

  async deleteCustomer(id: string): Promise<void> {
    await this.request(`/api/admin/customers/${id}`, { method: 'DELETE' });
  }

  async adminAddMember(customerId: string, email: string, role: string): Promise<AdminTeamMember> {
    return this.request(`/api/admin/customers/${customerId}/members`, {
      method: 'POST',
      body: JSON.stringify({ email, role }),
    });
  }

  async adminUpdateMemberRole(memberId: string, role: string): Promise<{ id: string; role: string }> {
    return this.request(`/api/admin/members/${memberId}`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    });
  }

  async adminRemoveMember(memberId: string): Promise<void> {
    await this.request(`/api/admin/members/${memberId}`, { method: 'DELETE' });
  }

  async getAdminIntegrations(params?: {
    page?: number;
    limit?: number;
  }): Promise<AdminIntegrationsResponse> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    const qs = query.toString();
    return this.request<AdminIntegrationsResponse>(`/api/admin/integrations${qs ? `?${qs}` : ''}`);
  }

  // ============ Course Sync ============

  async syncCourseForNewLessons(courseId: string, autoScrape = true): Promise<CourseSyncResult> {
    return this.request<CourseSyncResult>(`/api/dashboard/sync/course/${courseId}?auto_scrape=${autoScrape}`, { method: 'POST' });
  }

  async syncAllCourses(autoScrape = true): Promise<SyncAllCoursesResult> {
    return this.request<SyncAllCoursesResult>(`/api/dashboard/sync/all?auto_scrape=${autoScrape}`, { method: 'POST' });
  }

  async getSyncStatus(): Promise<{ lastRun: SyncLastRun | null }> {
    return this.request('/api/dashboard/sync/status');
  }

  // ============ Lesson Scraper ============

  async scrapeLessonContent(lessonId: string): Promise<ScrapedContent> {
    return this.request<ScrapedContent>(`/api/dashboard/scraper/lesson/${lessonId}`, { method: 'POST' });
  }

  async scrapeAllLessonsInCourse(courseId: string): Promise<{ jobId: string }> {
    return this.request<{ jobId: string }>(`/api/dashboard/scraper/course/${courseId}`, { method: 'POST' });
  }

  async getScrapeJobStatus(jobId: string): Promise<ScrapeJobStatus> {
    return this.request<ScrapeJobStatus>(`/api/dashboard/scraper/job/${jobId}`);
  }

  async scrapeAllLessons(courseId?: string): Promise<ScrapeAllResult> {
    const qs = courseId ? `?course_id=${courseId}` : '';
    return this.request<ScrapeAllResult>(`/api/dashboard/scraper/all${qs}`, { method: 'POST' });
  }

  async getScrapedContent(lessonId: string): Promise<ScrapedContent> {
    return this.request<ScrapedContent>(`/api/dashboard/scraper/content/${lessonId}`);
  }

  getScrapedMarkdownUrl(lessonId: string): string {
    const token = this.getToken();
    return `${API_URL}/api/dashboard/scraper/markdown/${lessonId}?token=${token}`;
  }

  // ============ CRM ============

  async getCRMConfig(): Promise<CRMConfig> {
    return this.request<CRMConfig>('/api/admin/crm/config');
  }

  async saveCRMConfig(config: Partial<CRMConfig>): Promise<CRMConfig> {
    return this.request<CRMConfig>('/api/admin/crm/config', {
      method: 'PUT',
      body: JSON.stringify(config),
    });
  }

  async fetchCFWorkspaces(apiToken: string, workspace: string): Promise<{ workspaces: CFWorkspaceOption[] }> {
    return this.request(`/api/admin/crm/clickfunnels/workspaces?api_token=${encodeURIComponent(apiToken)}&workspace=${encodeURIComponent(workspace)}`);
  }

  async fetchCFTags(apiToken: string, workspace: string, workspaceId: string): Promise<{ tags: CFTag[]; total: number }> {
    return this.request(`/api/admin/crm/clickfunnels/tags?api_token=${encodeURIComponent(apiToken)}&workspace=${encodeURIComponent(workspace)}&workspace_id=${encodeURIComponent(workspaceId)}`);
  }

  async testCRMConnection(): Promise<{ success: boolean; message: string }> {
    return this.request('/api/admin/crm/test', { method: 'POST' });
  }

  async syncUserToCRM(email: string, name: string, plan: string): Promise<{ success: boolean; error?: string }> {
    return this.request('/api/admin/crm/sync', {
      method: 'POST',
      body: JSON.stringify({ email, name, plan }),
    });
  }

  async syncAllUsersToCRM(): Promise<CRMSyncAllResult> {
    return this.request<CRMSyncAllResult>('/api/admin/crm/sync-all', { method: 'POST' });
  }

  async getCRMDeliveries(limit = 50): Promise<{ deliveries: CRMDelivery[] }> {
    return this.request(`/api/admin/crm/deliveries?limit=${limit}`);
  }

  // ============ Admin Settings ============

  async getStripeSettings(): Promise<StripeSettings> {
    return this.request<StripeSettings>('/api/admin/settings/stripe');
  }

  async saveStripeSettings(settings: Partial<StripeSettings>): Promise<StripeSettings> {
    return this.request<StripeSettings>('/api/admin/settings/stripe', {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  async getAdminTierConfig(): Promise<TierConfig> {
    return this.request<TierConfig>('/api/admin/settings/tiers');
  }

  async saveAdminTierConfig(config: Partial<TierConfig>): Promise<TierConfig> {
    return this.request<TierConfig>('/api/admin/settings/tiers', {
      method: 'PUT',
      body: JSON.stringify(config),
    });
  }

  async getWebhookLogs(limit = 50): Promise<{ logs: WebhookLog[] }> {
    return this.request(`/api/admin/settings/webhook-logs?limit=${limit}`);
  }

  async getAdminLimitsConfig(): Promise<LimitsConfig> {
    return this.request<LimitsConfig>('/api/admin/settings/limits');
  }

  async saveAdminLimitsConfig(config: Partial<LimitsConfig>): Promise<LimitsConfig> {
    return this.request<LimitsConfig>('/api/admin/settings/limits', {
      method: 'PUT',
      body: JSON.stringify(config),
    });
  }

  // ============ Billing ============

  async getSubscription(): Promise<Subscription> {
    const data = await this.request<{ subscription: Subscription }>('/api/dashboard/billing');
    return data.subscription;
  }

  async createCheckoutSession(plan: 'pro' | 'advanced' | 'platinum', interval: 'monthly' | 'yearly' = 'monthly'): Promise<{ url: string }> {
    return this.request('/api/dashboard/billing/checkout', {
      method: 'POST',
      body: JSON.stringify({ plan, interval }),
    });
  }

  async getTierConfig(): Promise<TierConfig> {
    return this.request<TierConfig>('/api/dashboard/billing/tiers');
  }

  async createPortalSession(): Promise<{ url: string }> {
    return this.request('/api/dashboard/billing/portal', {
      method: 'POST',
    });
  }
}

// Stripe admin settings
export interface StripeSettings {
  secret_key: string;
  webhook_secret: string;
  pro_monthly_price_id: string;
  pro_yearly_price_id: string;
  advanced_monthly_price_id: string;
  advanced_yearly_price_id: string;
  platinum_monthly_price_id: string;
  platinum_yearly_price_id: string;
}

// Tier configuration (pricing + features)
export interface TierConfig {
  pro_monthly_price: string;
  pro_yearly_price: string;
  advanced_monthly_price: string;
  advanced_yearly_price: string;
  platinum_monthly_price: string;
  platinum_yearly_price: string;
  pro_features: string;
  advanced_features: string;
  platinum_features: string;
}

// CRM types
export interface CRMConfig {
  platform: 'none' | 'clickfunnels' | 'gohighlevel';
  cf_api_token: string;
  cf_workspace: string;
  cf_workspace_id: string;
  cf_tag_pro: string;
  cf_tag_pro_id: string;
  cf_tag_advanced: string;
  cf_tag_advanced_id: string;
  cf_tag_platinum: string;
  cf_tag_platinum_id: string;
  ghl_webhook_url: string;
  ghl_location_id: string;
  ghl_tag_pro: string;
  ghl_tag_advanced: string;
  ghl_tag_platinum: string;
}

export interface CFTag {
  id: number;
  name: string;
  color?: string;
}

export interface CFWorkspaceOption {
  id: number;
  name: string;
  subdomain: string;
}

export interface CRMDelivery {
  id: string;
  platform: string;
  event_type: string;
  user_email: string;
  payload: string | null;
  status: string;
  status_code: number | null;
  response_body: string | null;
  error_message: string | null;
  created_at: string;
  delivered_at: string | null;
}

export interface CRMSyncAllResult {
  total: number;
  synced: number;
  failed: number;
  errors: string[];
}

// Video Media / Transcription types
export interface MediaPlugin {
  name: string;
  platform: string;
  displayName: string;
}

export interface MediaStats {
  total: number;
  by_platform: Array<{ platform: string; count: number }>;
  by_status: Array<{ status: string; count: number }>;
}

export interface DetectedVideoInfo {
  platform: string;
  videoId: string;
  embedUrl?: string;
  title?: string;
  thumbnail?: string;
  duration?: number;
}

export interface LessonMediaInfo {
  id: string;
  lesson_id: string;
  customer_id: string;
  platform: string;
  video_id: string;
  embed_url: string | null;
  title: string | null;
  thumbnail: string | null;
  duration: number | null;
  transcript_status: string;
  transcript_text: string | null;
  transcript_language: string | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

export interface TranscriptResultInfo {
  success: boolean;
  transcript?: string;
  language?: string;
  error?: string;
}

// Sync types
export interface CourseSyncResult {
  courseId: string;
  courseTitle: string;
  newLessons: number;
  scrapedLessons: number;
  errors: string[];
}

export interface SyncAllCoursesResult {
  synced: number;
  newLessonsTotal: number;
  scrapedTotal: number;
  courseResults: CourseSyncResult[];
  errors: string[];
}

export interface SyncLastRun {
  timestamp: string;
  customersProcessed: number;
  newLessonsTotal: number;
  scrapedTotal: number;
  errors: string[];
}

// Scraper types
export interface ScrapedContent {
  html: string;
  text: string;
  markdown: string;
  title: string;
  url: string;
  platform: string;
  wordCount: number;
  videosDetected: number;
  scrapedAt: string;
}

export interface ScrapeAllResult {
  total: number;
  succeeded: number;
  failed: number;
  errors: string[];
}

export interface ScrapeJobStatus {
  jobId: string;
  courseId: string;
  status: 'queued' | 'processing' | 'completed';
  total: number;
  completed: number;
  succeeded: number;
  failed: number;
  errors: string[];
  lessonStatuses: Record<string, 'pending' | 'scraping' | 'done' | 'error'>;
  createdAt: string;
  updatedAt: string;
}

// Admin types
export interface AdminPlatformStats {
  customers: {
    total: number;
    active: number;
    by_plan: { plan: string; count: number }[];
  };
  content: {
    courses: number;
    lessons: number;
    api_keys: number;
  };
  searches: {
    today: number;
    month: number;
  };
  integrations: {
    clickfunnels: number;
  };
}

export interface AdminCustomer {
  id: string;
  email: string;
  company_name: string;
  plan: string;
  is_admin: boolean;
  is_active: boolean;
  created_at: string;
  courses_count: number;
  lessons_count: number;
  api_keys_count: number;
  searches_30d: number;
}

export interface AdminTeamMember {
  id: string;
  user_id: string;
  email: string;
  name: string;
  role: 'owner' | 'admin' | 'editor' | 'viewer';
  is_active: boolean;
  invited_at: string;
  accepted_at: string | null;
}

export interface AdminCustomerDetail {
  id: string;
  email: string;
  company_name: string;
  plan: string;
  is_admin: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  stats: {
    courses: number;
    api_keys: number;
    domains: number;
    searches_30d: number;
    integrations: number;
  };
  members: AdminTeamMember[];
}

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  total_pages: number;
}

export interface AdminCustomersResponse {
  customers: AdminCustomer[];
  pagination: Pagination;
}

export interface AdminIntegration {
  id: string;
  name: string;
  workspace: string;
  is_connected: boolean;
  last_tested_at: string | null;
  created_at: string;
  customer_email: string;
  customer_company: string;
}

export interface AdminIntegrationsResponse {
  integrations: AdminIntegration[];
  pagination: Pagination;
}

export const api = new ApiClient();
